"use client";

import { motion } from "framer-motion";
import { ROUTES } from "@/app/(site)/_lib/routes";
import Image from "next/image";
import Link from "next/link";
import clsx from "clsx";
import { NewsPageQueryResult, SingleNewsPageQueryResult } from "@/sanity.types";
import { urlFor } from "@/sanity/lib/image";
import { hardcodedContent } from "@/app/(site)/_lib/global-content";
import { useIntersectionObserver } from "@/app/(site)/_hooks/useIntersectionObserver";

type NewsCardProps = {
	highlighted?: boolean;
	colSpan?: string; // Tailwind classes like "col-span-2 md:col-span-3 lg:col-span-4"
	colStart?: string; // Tailwind classes like "col-start-1 md:col-start-2"
	colEnd?: string; // Tailwind classes like "col-end-3 lg:col-end-4"
	news:
		| NonNullable<NewsPageQueryResult>["orderOfNewsList"][number]
		| NonNullable<SingleNewsPageQueryResult>["featuredNewsSection"]["news"][number];
	animationDelay?: number;
	className?: string;
};

export default function NewsCard({ highlighted = false, colSpan, colStart, colEnd, news, animationDelay = 0, className }: NewsCardProps) {
	const { ref, isVisible } = useIntersectionObserver({
		threshold: 0.2,
		triggerOnce: true,
		rootMargin: "50px",
	});

	return (
		<motion.article
			ref={ref}
			layoutId={news._id}
			className={clsx(
				"relative flex h-full flex-col",
				colSpan || (highlighted ? "col-span-2 sm:col-span-4" : "col-span-2 sm:col-span-3"),
				colStart,
				colEnd,
				className,
			)}
			initial={{ opacity: 0, y: 60 }}
			animate={{
				opacity: isVisible ? 1 : 0,
				y: isVisible ? 0 : 60,
			}}
			exit={{ opacity: 0, y: -30 }}
			transition={{
				duration: 0.3,
				ease: "easeOut",
				delay: isVisible ? animationDelay : 0,
			}}
		>
			<Link
				prefetch={false}
				href={ROUTES.NEWS_SINGLE(news.slug.current)}
				className="group"
				aria-label={`Read more about ${news.title}`}
			>
				<figure
					className={clsx(
						"relative mb-5 overflow-hidden rounded-sm",
						highlighted ? "aspect-[335/223] sm:aspect-[453/302]" : "aspect-[335/223] sm:aspect-[335/223]",
					)}
				>
					<Image
						src={urlFor(news.featuredImage).url()}
						alt={news.featuredImage.alt}
						fill
						className="transform rounded-sm object-cover transition-transform duration-500 ease-out group-hover:scale-105"
						sizes="(max-width: 989px) 95vw, 33vw"
					/>
					<div className="bg-graphite-black/20 absolute inset-0" />
				</figure>
				<div className="flex-grow">
					<div className="grid h-full grid-rows-[auto_1fr_auto]">
						<div className="min-h-[6.125rem]">
							<h4 className="text-graphite-black text-h6 mb-4 line-clamp-2 min-h-16 text-[1.75rem] leading-[110%] -tracking-[0.0175rem]">
								{news.title}
							</h4>
							<div className="relative flex items-center justify-between pb-5">
								{/* Fake border layer 1px */}
								<span className="bg-graphite-black-middle absolute bottom-0 left-0 h-[0.063rem] w-full transition-opacity duration-300 ease-in-out group-hover:opacity-0" />
								{/* Fake border layer 2px */}
								<span className="bg-brown-middle absolute bottom-0 left-0 h-[0.125rem] w-full origin-bottom scale-y-0 transition-transform duration-300 ease-in-out group-hover:scale-y-100" />

								<p className="text-cap-m text-graphite-black-middle z-10">{news.category.categoryName}</p>
								<p className="text-cap-m group-hover:text-brown-middle z-10 origin-right scale-90 uppercase opacity-0 transition-all duration-500 ease-in-out group-hover:scale-100 group-hover:opacity-100">
									{hardcodedContent.readMore}
								</p>
							</div>
						</div>
					</div>
				</div>
			</Link>
		</motion.article>
	);
}
